import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import classnames from "classnames";
import _ from "lodash";

import styles from "./blending.module.css";
import * as blendingActions from "../../redux/actions/blending";
import { useImgLoaded, useAudio } from "../../hooks/CustomHooks";
import { State } from "../../redux/store";
import LetterCard from "./LetterCard";
import { defaultWidth } from "../../util/Constants";
import { srcPath } from "../../util/Path";
import { blendingAudio } from "../../redux/actions/blending";

interface Props {
  itemLeft: number;
  ratio: number;
}

const GamePage = (props: Props) => {
  const { itemLeft, ratio } = props;
  const dispatch = useDispatch();
  const [imgLoaded, addImgLoaded] = useImgLoaded(2);
  const audioPlayed = useRef(false);

  const listRef = useRef(null);

  const { grade, unit } = useSelector((state: State) => state.shared);
  const { answer, answered, response, isSingleRow } = useSelector(
    (state: State) => state.blending
  );

  const groupLastIndex = [];

  let alphabetList = _.cloneDeep(
    useSelector((state: State) => state.blending.alphabetList)
  );
  if (alphabetList.length > 0 && Array.isArray(alphabetList[0])) {
    let tempIndex = -1;
    alphabetList = alphabetList.reduce((acc, cur) => {
      groupLastIndex.push((tempIndex += cur.length));
      return acc.concat(cur);
    }, []);
  }

  const answerString = Array.isArray(answer) ? answer.join("").replace(/#/g, '') : answer;

  const playAnswerAudio = useAudio(
    `blending/audios/${answerString}/Worksheet_Vocab_${answerString}_${answerString}.wav`
  );

  const speakerClick = () => {
    playAnswerAudio();
    if (!audioPlayed.current) {
      blendingAudio.src = null;
      blendingAudio.play();
      audioPlayed.current = true;
    }
  };

  const alphabetListStyle: React.CSSProperties = {
    left: `${itemLeft}px`,
    width: `${defaultWidth - itemLeft * 2}px`,
    padding: `0 ${alphabetList.length >= 10 ? 10 : 50 * ratio}px`,
    flexWrap: groupLastIndex.length ? "wrap" : "unset",
  };

  const boardWidth = `${495.5 * (isSingleRow ? 1.2 : 1.15)}px`;

  const boardStyle: React.CSSProperties = {
    width: answer && answer.length > 4 ? `${answered.length * 140}px` : boardWidth,
  };

  useEffect(() => {
    dispatch(blendingActions.setRound(1));
  }, [dispatch]);

  useEffect(() => {
    if (listRef.current) {
      const listHeight = listRef.current.offsetHeight;
      if (listHeight > 250) {
        isSingleRow && dispatch(blendingActions.setIsSingleRow(false));
      } else {
        !isSingleRow && dispatch(blendingActions.setIsSingleRow(true));
      }
    }
  });

  return (
    <div style={{ display: imgLoaded ? "block" : "none" }}>
      <div
        className={styles.alphabetList}
        style={alphabetListStyle}
        ref={listRef}
      >
        {alphabetList.map((alphabet, index) => (
          <LetterCard
            key={`alphabetList-${index}`}
            alphabet={alphabet}
            onBoard={false}
            index={index}
            style={{
              marginRight: groupLastIndex.includes(index) ? "80px" : null,
            }}
          />
        ))}
      </div>
      <div
        className={styles.lowerContainer}
        style={{
          left: `${itemLeft}px`,
          width: `${defaultWidth - itemLeft * 2}px`,
          top: `${isSingleRow ? 400 : 520}px`,
          height: `${isSingleRow ? 454 : 380}px`,
        }}
      >
        <div className={styles.answerContainer}>
          <TransitionGroup>
            <CSSTransition
              key={answer}
              timeout={300}
              classNames={{
                enter: styles.answerEnter,
                enterActive: styles.answerEnterActive,
                exit: styles.answerExit,
                exitActive: styles.answerExitActive,
              }}
            >
              <img
                className={styles.answer}
                src={srcPath(
                  `blending/words/${grade}.${unit}_${answerString}.png`
                )}
                alt=""
              />
            </CSSTransition>
          </TransitionGroup>
        </div>
        <div className={styles.boardContainer}>
          <img
            className={styles.board}
            src={srcPath("blending/board.png")}
            alt=""
            style={boardStyle}
            onLoad={addImgLoaded}
          />
          <div
            className={styles.boardWordCardContainer}
            style={{
              top: `${isSingleRow ? 35 : 20}px`,
              padding: `0 ${isSingleRow ? 50 : 10}px`,
              height: `${isSingleRow ? 265 : 240}px`,
            }}
          >
            {answered.map((alphabet, index) => (
              <LetterCard
                key={`boardWordCard-${index}`}
                alphabet={alphabet}
                onBoard={true}
                index={index}
              />
            ))}
            {response && (
              <img
                className={styles.response}
                src={srcPath(`shared/answer_${response}.png`)}
                alt=""
              />
            )}
          </div>
          <img
            className={classnames("abs", "btn", "main-btn", styles.speaker)}
            style={{
              bottom: `${isSingleRow ? 18 : 5}px`,
              transform: `scale(${isSingleRow ? 1 : 0.95})`,
            }}
            src={srcPath("shared/speaker.png")}
            alt=""
            onClick={speakerClick}
            onLoad={addImgLoaded}
          />
        </div>
      </div>
    </div>
  );
};

export default GamePage;
